<script setup>
import { ref } from 'vue';
import { API } from '@/services/index';
import { UPDATE_USER_PASSWORD } from '@/services/user/mutations/auth';
import { GET_EXTERNAL_AUTH_CONNECTIONS_QUERY } from '@/services/user/queries/auth';
import { CONNECT_EXTERNAL_OAUTH_URL, CONNECT_EXTERNAL_OAUTH_CALLBACK } from '@/services/user/mutations/auth';
import Cookie from 'js-cookie';
import { useMutation, useQuery } from '@tanstack/vue-query';
import HeaderPartial from '@/pages/partials/settings/headerPartial.vue';
import SectionPartial from '@/pages/partials/settings/sectionPartial.vue';
import googleIcon from '@/assets/social/Google.png';
import microsoftIcon from '@/assets/social/microsoft_icon.png';
import facebookIcon from '@/assets/social/Facebook_Logo_Primary.png';
import { useUserStore } from '@/stores/user.store.js';

const formRules = {
  password: (value) => !!value || 'Password is required',
  passwordRegex: (value) =>
    //  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&^_\-\+\(\)\#\=])[A-Za-z\d@$!%*?&^_\-\+\(\)\#\=]{8,}$/.test(value) ||
    useUserStore().passwordRegex.test(value) ||
    'Password must be a minimum of eight characters, at least one uppercase letter, one lowercase letter, one number and one special character',
  passwordConfirmation: (value) => !!value || 'Password confirmation is required',
  passwordConfirmationSame: (value) => value === password.value || 'Passwords do not match'
};

const isFormValid = ref(false);
const currentUser = computed(() => useUserStore().currentUser);
const password = ref(null);
const passwordConfirmation = ref(null);
const isPasswordVisible = ref(false);
const isConfirmPassVisible = ref(false);
const isPasswordFormValid = ref(false);
const isEditPassword = ref(false);
const isPasswordLoading = ref(false);
const updateForm = reactive({
  fieldType: null,
  isLoading: false
});
const isFacebookConnected = ref(false);
const isGoogleConnected = ref(false);
const isMicrosoftConnected = ref(false);
const isConnected = ref(false);
const connectedAuthConnections = ref(null);
const connectionAuthOptions = reactive([
  { provider: 'Meta', label: 'Facebook (Meta)', connected: false },
  { provider: 'Google', label: 'Google', connected: false },
  { provider: 'Microsoft', label: 'Microsoft', connected: false }
]);

// get external auth connections

// PUP-265 - disable external auths
// const {
//   isLoading: isLoadingExternalAuths,
//   isFetching: isFetchingExternalAuths,
//   error
// } = useQuery({
//   queryKey: ['extenalAuthConnections'],
//   queryFn: async () => {
//     const res = await API.sendQuery('/graphql/user', GET_EXTERNAL_AUTH_CONNECTIONS_QUERY);
//     if (res.data.data.externalAuth && res.data.data.externalAuth.length > 0) {
//       connectedAuthConnections.value = res.data.data.externalAuth.filter((connection) => {
//         return connection.connected === true;
//       });
//       return true;
//     }
//
//     if (res.data.error) {
//       console.log(res.data.error);
//       return false;
//     }
//
//     return false;
//   },
//   enabled: !!currentUser.value,
//   refetchOnWindowFocus: false,
//   refetchOnMount: true
// });

const passwordMutation = useMutation({
  mutationFn: async (payload) => {
    return await API.sendQuery('/graphql/user', UPDATE_USER_PASSWORD, payload);
  },
  onSuccess: (data) => {
    if (data.data.data.updatePassword) {
      isEditPassword.value = false;
      Cookie.remove('_pup_access_token');
      Cookie.set('_pup_access_token', data.data.data.updatePassword.token, {
        expires: 7,
        sameSite: 'strict',
        secure: true
      });
      isPasswordLoading.value = false;
      password.value = null;
      passwordConfirmation.value = null;
      isPasswordVisible.value = false;
      isConfirmPassVisible.value = false;
    }

    if (data.data.error) {
      // Helpers.showError(data.data.error.message);
      console.log(data.data.error);
      isPasswordLoading.value = false;
    }
    updateForm.isLoading = false;
  },
  onError: (error) => {
    console.log('error', error);
    isPasswordLoading.value = false;
    updateForm.fieldType = null;
    updateForm.isLoading = false;
  }
});

const updatePasswordHandler = async () => {
  if (isFormValid.value) {
    isPasswordLoading.value = true;
    updateForm.fieldType = 'password';
    updateForm.isLoading = true;
    const payload = {
      password: password.value,
      password_confirmation: passwordConfirmation.value
    };
    await passwordMutation.mutateAsync(payload);
  }
};

const oAuthMutationURL = useMutation({
  mutationFn: async (payload) => {
    return await API.sendQuery('/graphql/user', CONNECT_EXTERNAL_OAUTH_URL, payload);
  },
  onSuccess: (data) => {},
  onError: (error) => {
    console.log('error', error);
  }
});

const oAuthChangeHandle = async (channelType, isConnected) => {
  console.log('oAuthChangeHandle', channelType, isConnected);
  updateForm.isLoading = true;
  updateForm.fieldType = 'oAuth';

  if (isConnected) {
    // we need to remove the oauth connection from the user...
    console.log('remove the oauth connection from the user...');
  } else {
    await oAuthMutationURL.mutateAsync({ channel_type: channelType });
  }
};
</script>

<template>
  <div class="tw-max-w-[750px]">
    <HeaderPartial title="Password & Security"></HeaderPartial>
    <SectionPartial label="Manage Password" :isLoading="updateForm.isLoading && updateForm.fieldType === 'password'">
      <template v-if="!isEditPassword">
        <div class="tw-flex tw-items-center tw-justify-between">
          <p class="tw-mb-0 tw-text-sm tw-text-[#0d1216]">************************************</p>
          <v-btn class="tw-capitalize" variant="tonal" size="small" height="40px" @click="isEditPassword = true"> Edit </v-btn>
        </div>
      </template>
      <template v-else>
        <v-form ref="passwordForm" v-model="isFormValid" @submit.prevent="updatePasswordHandler">
          <div class="tw-flex tw-flex-col tw-items-start tw-justify-between lg:tw-flex-row lg:tw-space-x-2">
            <div class="tw-flex tw-w-full tw-flex-col">
              <v-text-field
                v-model="password"
                :type="isPasswordVisible ? 'text' : 'password'"
                :rules="[formRules.password, formRules.passwordRegex]"
                variant="outlined"
                color="primary-blue"
                placeholder="Password*"
                density="compact"
                :disabled="updateForm.isLoading && updateForm.fieldType === 'password'"
              >
                <template v-slot:append-inner>
                  <v-icon color="primary-blue" @click="isPasswordVisible = !isPasswordVisible" :style="isPasswordVisible ? 'opacity: 100%' : ''">
                    {{ isPasswordVisible ? 'mdi-eye-off' : 'mdi-eye' }}
                  </v-icon>
                </template>
              </v-text-field>
            </div>
            <div class="tw-flex tw-w-full tw-flex-col">
              <v-text-field
                v-model="passwordConfirmation"
                :type="isConfirmPassVisible ? 'text' : 'password'"
                :rules="[formRules.passwordConfirmation, formRules.passwordConfirmationSame]"
                variant="outlined"
                color="primary-blue"
                placeholder="Password Confirmation*"
                density="compact"
                :disabled="updateForm.isLoading && updateForm.fieldType === 'password'"
              >
                <template v-slot:append-inner>
                  <v-icon
                    color="primary-blue"
                    @click="isConfirmPassVisible = !isConfirmPassVisible"
                    :style="isConfirmPassVisible ? 'opacity: 100%' : ''"
                  >
                    {{ isConfirmPassVisible ? 'mdi-eye-off' : 'mdi-eye' }}
                  </v-icon>
                </template>
              </v-text-field>
            </div>
            <div class="tw-mt-3 tw-flex tw-w-full tw-items-center tw-justify-between lg:tw-ml-16 lg:tw-mt-0 lg:tw-w-[9rem] lg:tw-space-x-3">
              <v-btn
                class="tw-capitalize"
                variant="tonal"
                size="small"
                height="40px"
                @click="isEditPassword = false"
                :disabled="updateForm.isLoading && updateForm.fieldType === 'password'"
              >
                Cancel
              </v-btn>
              <v-btn
                class="tw-capitalize"
                variant="tonal"
                size="small"
                height="40px"
                type="submit"
                color="primary-blue"
                :loading="updateForm.isLoading && updateForm.fieldType === 'password'"
              >
                Save
              </v-btn>
            </div>
          </div>
        </v-form>
      </template>
    </SectionPartial>
    <!-- Disabled per PUP-265
    <SectionPartial label="Connected Accounts" subtitle="Connect your social connections like Google, Facebook, or Microsoft.">
      <v-progress-circular v-if="isLoadingExternalAuths || isFetchingExternalAuths" color="primary-blue" indeterminate></v-progress-circular>
      <template v-else>
        <template v-if="connectedAuthConnections">
          <div class="tw-flex tw-flex-col">
            <div class="tw-mb-2 tw-flex tw-items-center tw-justify-between">
              <div class="tw-flex">
                <img
                  class="tw-mr-2 tw-h-[30px] tw-w-[30px]"
                  :src="
                    connectedAuthConnections[0].service === 'Microsoft'
                      ? microsoftIcon
                      : connectedAuthConnections[0].service === 'Facebook'
                        ? facebookIcon
                        : googleIcon
                  "
                  :alt="connectedAuthConnections[0].service"
                />
                <div class="tw-ml-4 tw-flex tw-flex-col tw-text-left">
                  <div class="tw-flex tw-items-center tw-text-[0.875rem]">
                    <div>{{ connectedAuthConnections[0].service }}:</div>

                    <div class="tw-ml-2 tw-flex tw-font-medium">
                      <span class="tw-text-[0.8rem]">{{ currentUser.email }}</span>
                    </div>
                  </div>
                  <span class="tw-text-[0.8rem] tw-text-[#686767]">
                    {{
                      `Single Sign-On with ${connectedAuthConnections[0].service === 'Meta' ? `Facebook(Meta)` : connectedAuthConnections[0].service}`
                    }}
                  </span>
                </div>
              </div>
              <div class="tw-flex tw-justify-end">
                <v-switch
                  v-model="connectedAuthConnections[0].connected"
                  @update:modelValue="oAuthChangeHandle(connected[0].service, true)"
                  density="compact"
                  class="tw-mt-[-3px]"
                  color="secondary-blue"
                  hide-details
                  inset
                ></v-switch>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="tw-flex tw-flex-col tw-space-y-3">
            <div
              v-for="(connection, index) in connectionAuthOptions"
              :key="connection.provider"
              class="tw-mb-2 tw-flex tw-items-center tw-justify-between"
            >
              <div class="tw-flex tw-space-x-3">
                <img
                  class="tw-mr-2 tw-h-[30px] tw-w-[30px]"
                  :src="connection.provider === 'Microsoft' ? microsoftIcon : connection.provider === 'Google' ? googleIcon : facebookIcon"
                  :alt="connection.provider"
                />
                <div class="tw-flex tw-flex-col tw-text-left">
                  <div class="tw-flex tw-items-center tw-text-[0.875rem]">
                    <div>{{ connection.provider }}</div>
                  </div>
                  <span class="tw-text-[0.8rem] tw-text-[#686767]">
                    {{ `Single Sign-On with ${connection.provider === 'Meta' ? `Facebook(Meta)` : connection.provider}` }}
                  </span>
                </div>
              </div>
              <div class="tw-flex tw-justify-end">
                <v-switch
                  v-model="connection.connected"
                  @update:modelValue="oAuthChangeHandle(connection.provider, (connection.connected = !connection.connected))"
                  density="compact"
                  class="tw-mt-[-3px]"
                  color="secondary-blue"
                  hide-details
                  inset
                ></v-switch>
              </div>
            </div>
          </div>
        </template>
      </template>
    </SectionPartial>
    -->
    <SectionPartial
      :divider="false"
      label="Delete your account"
      subtitle="By deleting your account, you'll no longer be able login to PartnrUp and lose access to your workspaces & projects"
    >
      <div class="tw-mt-2 tw-flex tw-justify-end">
        <v-btn class="tw-capitalize" color="error" variant="flat" size="small" height="40px"> Delete account </v-btn>
      </div>
    </SectionPartial>
  </div>
</template>

<style scoped></style>
